import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { notNullOrEmpty, validEmail, validUKPhoneNumber, validateField } from "../../utils/validators";
import Comparer from "../../app/comparer";
import { FloatingEmailInput } from "../reusable-elements/floating-email-input";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import { RightAlignedButtons } from '../reusable-elements/right-aligned-buttons';

import './card-styles.css';

export const ContactCard = forwardRef(({ contact, cancelCallBack, handleChange, saveResult, showDelete }, ref) => {

    const comparer = new Comparer();

    const [state, setState] = useState(contact);
    const [originalState, setOriginalState] = useState(structuredClone(contact));
    const [validationErrors, setValidationErrors] = useState(null);
    const [rerender, setRerender] = useState(false);

    if (state.$id !== contact.$id && saveResult) {
        setState(structuredClone(contact));
        setOriginalState(structuredClone(contact));
        setRerender(!rerender);
    }

    useEffect(() => {
        if (!showDelete) {
            handleStateUpdates(v => { delete v.shouldDelete; return v; })
        }
    }, [showDelete]);

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.name), () => stateToValidate.name, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.email) && validEmail(stateToValidate.email), () => stateToValidate.email, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.phoneNumber) && validUKPhoneNumber(stateToValidate.phoneNumber), () => stateToValidate.phoneNumber, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.role), () => stateToValidate.role, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {
        setState(current => {
            delete current.saveResult;
            delete current.savingContact;
            const newState = updateMethod(current);
            validateState(newState);
            return newState;
        });
        setRerender(!rerender);
        handleChange();
    };

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal || contact.beingAdded;
    };

    const canSave = () => {
        if (contact.shouldDelete && contact.beingAdded) {
            return false;
        }
        return hasChanges() && Object.keys(validationErrors ?? {}).length === 0 && !state.savingContact;
    };

    // Expose methods to the parent component
    useImperativeHandle(ref, () => ({
        canSave: () => canSave(),
        getContact: () => state,
        setContact: contact => { setState(contact); setOriginalState(structuredClone(contact)); }
    }));

    const handleCancel = () => {
        handleStateUpdates(v => { v.shouldDelete = false; return v; })
        setState(structuredClone(originalState));
        setOriginalState(structuredClone(originalState));
        cancelCallBack(state);
        setRerender(!rerender);
        handleChange();
    };

    return !!state && (
        <div className='col-md-3 mt-10px'>
            <div style={{ borderRadius: "10px" }} className="card border-0 text-truncate bg-gray-200" >
                <div className="card-body">
                    <FloatingTextInput
                        className={"mt-1"}
                        label={`${!state.name ? "Enter" : ""} Name`}
                        text={state.name}
                        updateCallback={newValue => { handleStateUpdates(v => { v.name = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.name); }}
                    />
                    <FloatingEmailInput
                        className={"mt-1"}
                        label={`${!state.email ? "Enter" : ""} Email Address`}
                        email={state.email}
                        updateCallback={newValue => { handleStateUpdates(v => { v.email = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.email) && validEmail(state.email); }}
                    />
                    <FloatingTextInput
                        className={"mt-1"}
                        label={`${!state.phoneNumber ? "Enter" : ""} Phone Number`}
                        text={state.phoneNumber}
                        updateCallback={newValue => { handleStateUpdates(v => { v.phoneNumber = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return (notNullOrEmpty(state.phoneNumber) && validUKPhoneNumber(state.phoneNumber)); }}
                    />
                    <FloatingTextInput
                        className={"mt-1 mb-10px"}
                        label={`${!state.role ? "Enter" : ""} Role`}
                        text={state.role}
                        updateCallback={newValue => { handleStateUpdates(v => { v.role = newValue; return v; }) }}
                        required={false}
                        validatorFn={() => { return notNullOrEmpty(state.role); }}
                    />
                    {showDelete &&
                        <RightAlignedButtons>
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="checkbox" id={`delete-${contact.$id}`} checked={state.shouldDelete} onChange={() => { handleStateUpdates(v => { if (v.shouldDelete) { delete v.shouldDelete } else { v.shouldDelete = true }; return v; }) }} />
                                <label className="form-check-label text-black" htmlFor={`delete-${contact.$id}`}>Delete</label>
                            </div>
                        </RightAlignedButtons>}
                    {hasChanges() &&
                        <RightAlignedButtons>
                            <button title='Cancel' className="btn btn-warning" onClick={handleCancel}>
                                <i title='Cancel' className="fa fa-cancel"></i>&nbsp;&nbsp;Cancel
                            </button>
                        </RightAlignedButtons>
                    }
                </div>
            </div>
        </div>
    );
});